import React, { useState, FormEvent, useEffect } from 'react';
import { Card, DatePicker, Select, Button, message, Typography, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { DatePickerProps } from 'antd';
import dayjs from 'dayjs';
import EmailInput from '../components/EmailInput/EmailInput';
import { EchoLanguage, EchoSpeaker, CreateEchoRequest } from '../types/echo';
import type { EmailRecipient } from '../types/email';
import type { Product } from '../types/product';
import ApiService from '../service/api-service';

const { Option } = Select;
const { Title } = Typography;

interface FormData {
  title: string;
  description: string;
  productId: string;
  language: EchoLanguage;
  speakerGender: EchoSpeaker;
  validity: dayjs.Dayjs | null;
  recipients: EmailRecipient[];
  showAvatar: boolean;
}

interface FormErrors {
  title?: string;
  description?: string;
  productId?: string;
  language?: string;
  speakerGender?: string;
  validity?: string;
  recipients?: string;
}

const NewEcho: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    productId: '',
    language: EchoLanguage.ENGLISH,
    speakerGender: EchoSpeaker.MALE,
    validity: null,
    recipients: [],
    showAvatar: false
  });
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await ApiService.getProducts();
        setProducts(response.data.products);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        messageApi.error('Failed to load products');
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [messageApi]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Please input the echo name!';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Please input the description!';
    }
    
    if (!formData.productId) {
      newErrors.productId = 'Please select a product!';
    }
    
    if (!formData.language) {
      newErrors.language = 'Please select a language!';
    }
    
    if (!formData.speakerGender) {
      newErrors.speakerGender = 'Please select speaker gender!';
    }
    
    if (!formData.validity) {
      newErrors.validity = 'Please select the validity!';
    }
    
    if (formData.recipients.length === 0) {
      newErrors.recipients = 'Please add at least one recipient!';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDateChange: DatePickerProps['onChange'] = (date) => {
    setFormData(prev => ({ ...prev, validity: date }));
    if (errors.validity) {
      setErrors(prev => ({ ...prev, validity: undefined }));
    }
  };

  // Disable dates before today
  const disabledDate = (current: dayjs.Dayjs) => {
    return current && current < dayjs().startOf('day');
  };

  // Disable past times for today's date
  const disabledTime = (current: dayjs.Dayjs | null) => {
    if (current && current.isSame(dayjs(), 'day')) {
      const currentHour = dayjs().hour();
      const currentMinute = dayjs().minute();
      const currentSecond = dayjs().second();

      return {
        disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
        disabledMinutes: (selectedHour: number) => 
          selectedHour === currentHour 
            ? Array.from({ length: currentMinute }, (_, i) => i)
            : [],
        disabledSeconds: (selectedHour: number, selectedMinute: number) => 
          selectedHour === currentHour && selectedMinute === currentMinute
            ? Array.from({ length: currentSecond }, (_, i) => i)
            : []
      };
    }
    return {};
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const request: CreateEchoRequest = {
        name: formData.title,
        description: formData.description,
        productId: formData.productId,
        language: formData.language,
        speaker: formData.speakerGender,
        validTill: formData.validity!.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        showAvatar: formData.showAvatar,
        audiences: formData.recipients.map(r => ({
          firstName: r.firstName,
          lastName: r.lastName,
          email: r.email
        }))
      };

      const response = await ApiService.createEcho(request);
      
      if (response.success) {
        // Navigate to success page with echo details
        navigate('/echo-success', {
          state: {
            echoName: formData.title,
            recipientCount: formData.recipients.length
          }
        });
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Failed to create echo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {contextHolder}
      <div className="mb-8">
        <Title level={4} className="text-gray-800 mb-2">Create New Echo</Title>
        <p className="text-gray-500">Create a new interview practice session with customized settings</p>
      </div>

      <Card className="shadow-sm rounded-lg border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <label className="block">
              <span className="text-gray-700 text-sm font-medium">Name</span>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                placeholder="Enter echo name"
              />
              {errors.title && (
                <span className="text-sm text-red-500 mt-1">{errors.title}</span>
              )}
            </label>

            <label className="block">
              <span className="text-gray-700 text-sm font-medium">Description</span>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 resize-none"
                placeholder="Enter echo description"
              />
              {errors.description && (
                <span className="text-sm text-red-500 mt-1">{errors.description}</span>
              )}
            </label>

            <div className="block space-y-1">
              <span className="text-gray-700 text-sm font-medium">Product</span>
              <Select
                value={formData.productId}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, productId: value }));
                  if (errors.productId) setErrors(prev => ({ ...prev, productId: undefined }));
                }}
                className="mt-1 block w-full"
                placeholder="Select product"
                loading={loadingProducts}
                status={errors.productId ? "error" : undefined}
                rootClassName="!mb-0"
              >
                {products.map(product => (
                  <Option key={product.id} value={product.id}>
                    {product.name}
                  </Option>
                ))}
              </Select>
              {errors.productId && (
                <div className="text-sm text-red-500 min-h-[20px]">{errors.productId}</div>
              )}
            </div>

            <div className="block space-y-1">
              <span className="text-gray-700 text-sm font-medium">Speaker Gender</span>
              <Select
                value={formData.speakerGender}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, speakerGender: value }));
                  if (errors.speakerGender) setErrors(prev => ({ ...prev, speakerGender: undefined }));
                }}
                className="mt-1 block w-full"
                placeholder="Select speaker gender"
                status={errors.speakerGender ? "error" : undefined}
                rootClassName="!mb-0"
              >
                {Object.values(EchoSpeaker).map(speaker => (
                  <Option key={speaker} value={speaker}>
                    {speaker.charAt(0) + speaker.slice(1).toLowerCase()}
                  </Option>
                ))}
              </Select>
              {errors.speakerGender && (
                <div className="text-sm text-red-500 min-h-[20px]">{errors.speakerGender}</div>
              )}
            </div>

            <div className="block space-y-1">
              <span className="text-gray-700 text-sm font-medium">Language</span>
              <Select
                value={formData.language}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, language: value }));
                  if (errors.language) setErrors(prev => ({ ...prev, language: undefined }));
                }}
                className="mt-1 block w-full"
                placeholder="Select language"
                status={errors.language ? "error" : undefined}
                rootClassName="!mb-0"
              >
                {Object.values(EchoLanguage).map(language => (
                  <Option key={language} value={language}>
                    {language.charAt(0) + language.slice(1).toLowerCase()}
                  </Option>
                ))}
              </Select>
              {errors.language && (
                <div className="text-sm text-red-500 min-h-[20px]">{errors.language}</div>
              )}
            </div>

            <div className="block space-y-1">
              <span className="text-gray-700 text-sm font-medium flex items-center gap-2">
                Show Avatar
                <Tooltip title="When enabled, an AI-generated avatar will be displayed during the feedback session, making the experience more engaging and personal for the user">
                  <InfoCircleOutlined className="text-gray-400 hover:text-gray-600 cursor-help" />
                </Tooltip>
              </span>
              <Select
                value={formData.showAvatar}
                onChange={(value) => {
                  setFormData(prev => ({ ...prev, showAvatar: value }));
                }}
                className="mt-1 block w-full"
                placeholder="Select avatar visibility"
                rootClassName="!mb-0"
              >
                <Option value={false}>No</Option>
                <Option value={true}>Yes</Option>
              </Select>
            </div>

            <div className="block">
              <span className="text-gray-700 text-sm font-medium">Validity</span>
              <DatePicker
                value={formData.validity}
                onChange={handleDateChange}
                className="mt-1 block w-full"
                showTime
                format="YYYY-MM-DD HH:mm:ss"
                disabledDate={disabledDate}
                disabledTime={disabledTime}
              />
              {errors.validity && (
                <span className="text-sm text-red-500 mt-1">{errors.validity}</span>
              )}
            </div>

            <div className="block">
              <span className="text-gray-700 text-sm font-medium">Recipients</span>
              <div className="mt-1">
                <EmailInput
                  value={formData.recipients}
                  onChange={(recipients) => {
                    setFormData(prev => ({ ...prev, recipients }));
                    if (errors.recipients) setErrors(prev => ({ ...prev, recipients: undefined }));
                  }}
                />
              </div>
              {errors.recipients && (
                <span className="text-sm text-red-500 mt-1">{errors.recipients}</span>
              )}
            </div>
          </div>

          <div className="pt-4">
            <Button
              type="primary"
              htmlType="submit"
              className="bg-cyan-600 hover:bg-cyan-500 !rounded-lg px-6"
              loading={loading}
            >
              Create Echo
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default NewEcho; 